import { Link } from 'react-router-dom';
import { Button } from '../components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { ArrowLeft, ArrowRight, Hammer, Paintbrush, Truck, BookOpen, Users, Heart, Library, Palette, Camera, Share2, DollarSign, ClipboardList } from 'lucide-react';

const LOGO_URL = "https://customer-assets.emergentagent.com/job_ab04b277-779a-46f6-a15c-41b90e7d7254/artifacts/ccdpbqku_TBP-New%20Logo%20Design%20conv%201.png";

const volunteerRoles = [
  {
    id: 'bookshelf-assembler',
    title: 'Bookshelf Assembler',
    subtitle: 'Construction & Assembly',
    icon: Hammer,
    color: 'bg-amber-500',
    purpose: 'Build sturdy, beautiful bookshelves that will become the foundation of in-home libraries for children and families in our community.',
    responsibilities: [
      'Assemble bookshelves following provided instructions and templates',
      'Sand, finish, and prepare bookshelves for painting or staining',
      'Work collaboratively with other volunteers during build days',
      'Ensure all shelves meet quality and safety standards',
      'Assist with organizing tools and cleaning up the workspace'
    ],
    skills: [
      'Basic carpentry or woodworking experience (helpful but not required)',
      'Ability to follow instructions and use hand/power tools safely',
      'Physical ability to lift up to 30 lbs',
      'Team collaboration and communication',
      'Attention to detail and quality'
    ],
    impact: 'Each bookshelf you build becomes a permanent fixture in a child\'s home—a place where stories come alive and a love of reading begins.'
  },
  {
    id: 'bookshelf-artisan',
    title: 'Bookshelf Artisan',
    subtitle: 'Creative Design Volunteer',
    icon: Palette,
    color: 'bg-pink-500',
    purpose: 'Bring creativity and joy to The Bookshelf Project by decorating and personalizing bookshelves that will become the foundation of a child\'s in-home library. Bookshelf Artisans help transform bookshelves into vibrant literacy spaces that inspire children to read, imagine, and learn.',
    responsibilities: [
      'Paint and decorate bookshelves using safe, child-friendly materials',
      'Create colorful, engaging designs that appeal to young readers',
      'Apply themes, patterns, or literacy-inspired artwork to bookshelves',
      'Ensure all finished bookshelves are smooth, safe, and free of hazards',
      'Collaborate with other volunteers to maintain consistent quality and design standards',
      'Assist with organizing painting supplies and workspace cleanup'
    ],
    skills: [
      'Creativity and artistic ability',
      'Attention to detail and craftsmanship',
      'Experience with painting, design, or crafting (helpful but not required)',
      'Ability to follow safety guidelines and use materials responsibly'
    ],
    impact: 'Bookshelf Artisans help transform bookshelves into vibrant literacy spaces that inspire children to read, imagine, and learn.'
  },
  {
    id: 'bookmobile-navigator',
    title: 'Bookmobile Navigator',
    subtitle: 'Distribution & Delivery',
    icon: Truck,
    color: 'bg-blue-500',
    purpose: 'Bring the gift of literacy directly to families by delivering bookshelves and books to homes throughout the community.',
    responsibilities: [
      'Load bookshelves and book bundles into delivery vehicles',
      'Transport materials safely to family homes',
      'Set up bookshelves in designated locations within homes',
      'Interact warmly and respectfully with families during deliveries',
      'Document deliveries with photos (with family permission)',
      'Report any issues or special family needs to program coordinators'
    ],
    skills: [
      'Valid driver\'s license and clean driving record',
      'Ability to lift up to 50 lbs',
      'Reliable transportation (or ability to drive organization vehicles)',
      'Strong interpersonal and communication skills',
      'Punctuality and reliability'
    ],
    impact: 'Bookmobile Navigators are often the face of The Bookshelf Project to families. Your warm presence and care make the delivery experience memorable and meaningful.'
  },
  {
    id: 'book-curator',
    title: 'Book Curator',
    subtitle: 'Book Selection & Inventory Support',
    icon: Library,
    color: 'bg-emerald-500',
    purpose: 'Ensure families receive high-quality, culturally relevant, and age-appropriate books that support early literacy development.',
    responsibilities: [
      'Sort and organize donated books by age group and reading level',
      'Assist with curating book bundles for families (birth–age 8)',
      'Maintain book inventory and report low stock levels',
      'Support book labeling and packaging for distribution',
      'Identify culturally relevant titles that reflect the families served'
    ],
    skills: [
      'Interest in children\'s literature',
      'Organization and attention to detail',
      'Background in education, libraries, or literacy programs (helpful but not required)'
    ],
    impact: 'Book Curators ensure every child receives books that speak to their experiences, spark their imagination, and meet them at their reading level.'
  },
  {
    id: 'literacy-ambassador',
    title: 'Literacy Ambassador',
    subtitle: 'Community Outreach & Education',
    icon: BookOpen,
    color: 'bg-purple-500',
    purpose: 'Spread the word about The Bookshelf Project and the importance of early childhood literacy in our community.',
    responsibilities: [
      'Represent the organization at community events and fairs',
      'Share information about our programs with potential families and donors',
      'Help staff information tables and distribute materials',
      'Engage with community members about the importance of reading',
      'Support social media and awareness campaigns'
    ],
    skills: [
      'Excellent communication and public speaking skills',
      'Passion for literacy and community service',
      'Comfortable engaging with diverse groups of people',
      'Social media savvy (helpful for digital outreach)',
      'Bilingual abilities are a plus'
    ],
    impact: 'Literacy Ambassadors expand our reach and help connect more families with the resources they need to build readers.'
  },
  {
    id: 'photographer-videographer',
    title: 'Photographer/Videographer',
    subtitle: 'Visual Storytelling',
    icon: Camera,
    color: 'bg-indigo-500',
    purpose: 'Capture the moments that tell our story—documenting the impact of The Bookshelf Project through powerful images and videos that inspire donors, volunteers, and the community.',
    responsibilities: [
      'Photograph volunteer events, deliveries, and community programs',
      'Capture candid moments of families receiving bookshelves and books',
      'Create short video content for social media and donor communications',
      'Edit and organize photos for organizational archives',
      'Ensure all photography follows consent and privacy guidelines',
      'Support marketing materials with high-quality visual content'
    ],
    skills: [
      'Photography and/or videography experience',
      'Proficiency with camera equipment and editing software',
      'Ability to capture authentic, emotional moments',
      'Understanding of consent and privacy when photographing families',
      'Creative eye for storytelling through visuals'
    ],
    impact: 'Your images and videos help share our mission with the world, inspiring more people to support literacy and transforming moments into lasting memories for families.'
  },
  {
    id: 'social-media-marketing',
    title: 'Social Media & Marketing',
    subtitle: 'Digital Outreach & Communications',
    icon: Share2,
    color: 'bg-sky-500',
    purpose: 'Amplify The Bookshelf Project\'s message and reach new audiences through compelling digital content and strategic social media engagement.',
    responsibilities: [
      'Create engaging social media posts for Facebook, Instagram, and LinkedIn',
      'Develop content calendars and posting schedules',
      'Respond to comments and messages on social platforms',
      'Write blog posts, newsletters, and email campaigns',
      'Track engagement metrics and report on campaign performance',
      'Support branding consistency across all communications'
    ],
    skills: [
      'Experience with social media platforms and content creation',
      'Strong writing and copyediting skills',
      'Basic graphic design (Canva or similar tools)',
      'Understanding of digital marketing best practices',
      'Creativity and ability to tell compelling stories'
    ],
    impact: 'Social Media & Marketing volunteers help spread our mission far and wide, connecting us with new donors, volunteers, and families who need our support.'
  },
  {
    id: 'fundraising-support',
    title: 'Fundraising Support',
    subtitle: 'Donor Relations & Development',
    icon: DollarSign,
    color: 'bg-green-500',
    purpose: 'Help secure the resources needed to build more bookshelves and reach more families by supporting fundraising initiatives and donor relations.',
    responsibilities: [
      'Research potential corporate sponsors and grant opportunities',
      'Assist with donor database management and updates',
      'Help prepare fundraising materials and proposals',
      'Support donor appreciation and stewardship activities',
      'Assist with planning and executing fundraising events',
      'Write thank-you letters and donor acknowledgments'
    ],
    skills: [
      'Strong research and writing skills',
      'Experience with databases and spreadsheets',
      'Understanding of nonprofit fundraising (helpful but not required)',
      'Attention to detail and organization',
      'Professional communication skills'
    ],
    impact: 'Fundraising volunteers directly enable our mission by helping secure the resources needed to build bookshelves, buy books, and reach more children.'
  },
  {
    id: 'volunteer-coordinator',
    title: 'Volunteer Coordinator',
    subtitle: 'Volunteer Management & Support',
    icon: ClipboardList,
    color: 'bg-orange-500',
    purpose: 'Help recruit, train, and support fellow volunteers to ensure The Bookshelf Project runs smoothly and every volunteer has a positive experience.',
    responsibilities: [
      'Assist with volunteer recruitment and onboarding',
      'Help schedule volunteers for events and activities',
      'Serve as a point of contact for volunteer questions and concerns',
      'Lead volunteer orientations and training sessions',
      'Track volunteer hours and recognition milestones',
      'Support volunteer appreciation and retention initiatives'
    ],
    skills: [
      'Strong organizational and communication skills',
      'Experience with volunteer management or HR (helpful)',
      'Leadership and people skills',
      'Ability to multitask and problem-solve',
      'Passion for supporting and empowering others'
    ],
    impact: 'Volunteer Coordinators ensure that every person who gives their time to The Bookshelf Project feels valued, supported, and connected to our mission.'
  },
  {
    id: 'event-volunteer',
    title: 'Event Volunteer',
    subtitle: 'Program & Event Support',
    icon: Users,
    color: 'bg-cyan-500',
    purpose: 'Support the success of literacy events, family workshops, and community programs that bring books and families together.',
    responsibilities: [
      'Assist with event setup and breakdown',
      'Greet and guide families at events',
      'Help facilitate reading activities and crafts',
      'Manage registration tables and distribute materials',
      'Support photographers and capture moments (with permission)'
    ],
    skills: [
      'Friendly and welcoming demeanor',
      'Ability to work in fast-paced environments',
      'Flexibility and willingness to help where needed',
      'Experience working with children and families (helpful)',
      'Strong organizational skills'
    ],
    impact: 'Event volunteers create memorable experiences that reinforce the joy of reading and strengthen our connection with families.'
  },
  {
    id: 'administrative-support',
    title: 'Administrative Support',
    subtitle: 'Office & Operations',
    icon: Heart,
    color: 'bg-rose-500',
    purpose: 'Keep The Bookshelf Project running smoothly behind the scenes with essential administrative and operational support.',
    responsibilities: [
      'Assist with data entry and database management',
      'Help prepare mailings, thank-you letters, and donor communications',
      'Support scheduling and volunteer coordination',
      'Organize supplies and maintain inventory records',
      'Assist with grant research and reporting'
    ],
    skills: [
      'Proficiency with Microsoft Office or Google Workspace',
      'Strong attention to detail and accuracy',
      'Excellent written communication skills',
      'Ability to handle confidential information appropriately',
      'Self-motivated and able to work independently'
    ],
    impact: 'Administrative volunteers ensure our programs run efficiently, allowing us to serve more families with fewer resources.'
  }
];

export default function VolunteerRoles() {
  return (
    <div className="min-h-screen bg-gradient-to-b from-slate-50 to-white" data-testid="volunteer-roles-page">
      {/* Navigation */}
      <nav className="bg-white shadow-sm sticky top-0 z-50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex justify-between items-center h-16">
            <div className="flex items-center gap-3">
              <Link to="/">
                <img src={LOGO_URL} alt="The Bookshelf Project" className="h-12 w-auto" />
              </Link>
            </div>
            <div className="flex items-center gap-4">
              <Link to="/">
                <Button variant="ghost" className="text-slate-600 hover:text-[#1a3a5c]">
                  <ArrowLeft className="w-4 h-4 mr-2" />
                  Back to Home
                </Button>
              </Link>
              <Link to="/apply">
                <Button className="bg-[#1a3a5c] hover:bg-[#1a3a5c]/90 text-white rounded-full px-6" data-testid="apply-btn">
                  Apply Now
                </Button>
              </Link>
            </div>
          </div>
        </div>
      </nav>

      {/* Hero Section */}
      <section className="py-16 bg-gradient-to-br from-[#1a3a5c] to-[#2a5a8c]">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <h1 className="text-4xl sm:text-5xl font-bold text-white mb-4" style={{ fontFamily: 'Manrope, sans-serif' }}>
            Volunteer Opportunities
          </h1>
          <p className="text-xl text-white/80 max-w-3xl mx-auto">
            Find the perfect way to contribute your skills and make a lasting impact on children's literacy in our community.
          </p>
        </div>
      </section>

      {/* Roles Grid */}
      <section className="py-16">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="space-y-12">
            {volunteerRoles.map((role, index) => {
              const IconComponent = role.icon;
              return (
                <Card key={role.id} className="border-0 shadow-lg overflow-hidden" data-testid={`role-${role.id}`}>
                  <div className="md:flex">
                    {/* Left Side - Role Header */}
                    <div className={`md:w-1/3 ${role.color} p-8 text-white flex flex-col items-center justify-center text-center`}>
                      <div className="w-16 h-16 bg-white/20 rounded-2xl flex items-center justify-center mb-4">
                        <IconComponent className="w-8 h-8" />
                      </div>
                      <h2 className="text-2xl font-bold mb-1" style={{ fontFamily: 'Manrope, sans-serif' }}>
                        {role.title}
                      </h2>
                      <p className="text-white/80 text-sm font-medium">{role.subtitle}</p>
                    </div>
                    
                    {/* Right Side - Role Details */}
                    <div className="md:w-2/3 p-8">
                      {/* Purpose */}
                      <div className="mb-6">
                        <h3 className="text-lg font-semibold text-[#1a3a5c] mb-2">Role Purpose</h3>
                        <p className="text-slate-600 leading-relaxed">{role.purpose}</p>
                      </div>
                      
                      {/* Two Column Layout for Responsibilities and Skills */}
                      <div className="grid md:grid-cols-2 gap-6 mb-6">
                        {/* Responsibilities */}
                        <div>
                          <h3 className="text-lg font-semibold text-[#1a3a5c] mb-3">Key Responsibilities</h3>
                          <ul className="space-y-2">
                            {role.responsibilities.map((resp, i) => (
                              <li key={i} className="flex items-start gap-2 text-sm text-slate-600">
                                <span className="w-1.5 h-1.5 rounded-full bg-[#5cc8e8] mt-2 flex-shrink-0"></span>
                                {resp}
                              </li>
                            ))}
                          </ul>
                        </div>
                        
                        {/* Skills */}
                        <div>
                          <h3 className="text-lg font-semibold text-[#1a3a5c] mb-3">Skills Helpful for This Role</h3>
                          <ul className="space-y-2">
                            {role.skills.map((skill, i) => (
                              <li key={i} className="flex items-start gap-2 text-sm text-slate-600">
                                <span className="w-1.5 h-1.5 rounded-full bg-[#F4D03F] mt-2 flex-shrink-0"></span>
                                {skill}
                              </li>
                            ))}
                          </ul>
                        </div>
                      </div>
                      
                      {/* Impact */}
                      <div className="bg-slate-50 rounded-xl p-4 border-l-4 border-[#5cc8e8]">
                        <h3 className="text-sm font-semibold text-[#1a3a5c] mb-1">Impact of This Role</h3>
                        <p className="text-sm text-slate-600">{role.impact}</p>
                      </div>
                    </div>
                  </div>
                </Card>
              );
            })}
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="py-16 bg-[#1a3a5c]">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <h2 className="text-3xl font-bold text-white mb-4" style={{ fontFamily: 'Manrope, sans-serif' }}>
            Ready to Make a Difference?
          </h2>
          <p className="text-white/80 text-lg mb-8">
            Join our community of volunteers and help build a future where every child has access to books and a love of reading.
          </p>
          <Link to="/apply">
            <Button 
              size="lg" 
              className="bg-[#5cc8e8] hover:bg-[#5cc8e8]/90 text-[#1a3a5c] font-semibold rounded-full px-8 py-4 text-lg"
              data-testid="apply-now-btn"
            >
              Start Your Application
              <ArrowRight className="ml-2 w-5 h-5" />
            </Button>
          </Link>
        </div>
      </section>

      {/* Footer */}
      <footer className="bg-[#1a3a5c] border-t border-white/10 py-8">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex flex-col md:flex-row justify-between items-center gap-4">
            <div className="flex items-center gap-3">
              <Link to="/" className="text-white font-bold text-lg" style={{ fontFamily: 'Manrope, sans-serif' }}>
                The Bookshelf Project
              </Link>
            </div>
            <p className="text-white/60 text-sm">
              © {new Date().getFullYear()} The Bookshelf Project. Building in-home libraries for children.
            </p>
          </div>
        </div>
      </footer>
    </div>
  );
}
